// Add or edit a test type. Admin only, Pattern C at 640px.

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { listTestTypes, createTestType, updateTestType } from '../api.js'
import { useApiForm, blankToNull } from '../useApiForm.js'
import { TEST_CATEGORIES, SPECIMEN_TYPES, formatPrice } from '../vocab.js'
import { Button, ButtonLink, Input, Notice, Panel, Select } from '../components/ui/index.js'
import { fieldFactory } from '../formField.jsx'

const EMPTY = {
  code: '',
  name: '',
  category: 'NON_DOT_RAPID_URINE',
  panelSize: '',
  specimenType: 'URINE',
  hasLabCogs: false,
  defaultUnitPrice: '',
  defaultCogs: '',
  hourlyMinimum: '',
  hourlyRate: '',
  notes: '',
}

// An onsite fee is priced as hourly minimum times hourly rate, so the price
// input gives way to those two and the server calculates the total.
function toPayload(form) {
  const payload = blankToNull(form)
  payload.code = form.code
  payload.name = form.name
  payload.category = form.category
  payload.specimenType = form.specimenType
  payload.hasLabCogs = form.hasLabCogs
  if (form.category === 'ONSITE') {
    payload.defaultUnitPrice = null
  } else {
    payload.hourlyMinimum = null
    payload.hourlyRate = null
  }
  return payload
}

export default function TestForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const [form, setForm] = useState(isEdit ? null : EMPTY)
  const [loadError, setLoadError] = useState(null)
  const { saving, error, fieldErrors, submit } = useApiForm()

  useEffect(() => {
    if (!isEdit) return
    listTestTypes()
      .then((data) => {
        const testType = data.testTypes.find((row) => row.id === id)
        if (!testType) throw new Error('That test type does not exist.')
        const filled = { ...EMPTY }
        for (const key of Object.keys(EMPTY)) {
          filled[key] = testType[key] ?? EMPTY[key]
        }
        setForm(filled)
      })
      .catch((caught) => setLoadError(caught.message))
  }, [id, isEdit])

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function onSubmit(event) {
    event.preventDefault()
    const payload = toPayload(form)
    const result = await submit(() =>
      isEdit ? updateTestType(id, payload) : createTestType(payload),
    )
    if (result) navigate('/tests')
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }
  if (!form) return <div className="center-note">Loading</div>

  const field = fieldFactory({ form, change, fieldErrors })

  return (
    <div className="form-page">
      <header className="page-head">
        <h1>{isEdit ? 'Edit test type' : 'Add a test type'}</h1>
      </header>

      <Panel>
        <form onSubmit={onSubmit} noValidate>
          {error ? <Notice tone="bad">{error}</Notice> : null}

          <div className="formgrid">
            {field('code', 'Code', {
              help: 'Short and unique, like RU-10. Shows in tables and exports.',
            })}
            {field('name', 'Test name')}

            <Select
              id="category"
              label="Category"
              value={form.category}
              onChange={(category) => change('category', category)}
              options={TEST_CATEGORIES.map((category) => ({
                value: category.value,
                label: category.label,
              }))}
            />

            {field('panelSize', 'Panel size', {
              type: 'number',
              min: '1',
              help: 'Leave blank where a panel does not apply.',
            })}

            <Select
              id="specimenType"
              label="Specimen"
              value={form.specimenType}
              onChange={(specimenType) => change('specimenType', specimenType)}
              options={SPECIMEN_TYPES.map((specimen) => ({
                value: specimen.value,
                label: specimen.label,
              }))}
            />

            <Select
              id="hasLabCogs"
              label="Lab cost of goods"
              value={form.hasLabCogs}
              onChange={(hasLabCogs) => change('hasLabCogs', hasLabCogs)}
              options={[
                { value: false, label: 'No lab cost' },
                { value: true, label: 'Carries a lab cost' },
              ]}
            />

            {form.category === 'ONSITE' ? (
              <>
                {field('hourlyMinimum', 'Hourly minimum', {
                  type: 'number',
                  step: '0.25',
                  min: '0',
                  help: 'The fewest hours an onsite is charged for.',
                })}
                {field('hourlyRate', 'Hourly rate', {
                  type: 'number',
                  step: '0.01',
                  min: '0',
                  help:
                    form.hourlyMinimum && form.hourlyRate
                      ? `Onsite total: ${formatPrice(Number(form.hourlyMinimum) * Number(form.hourlyRate))}. Becomes this item's price.`
                      : 'Total is hourly minimum times hourly rate.',
                })}
              </>
            ) : (
              field('defaultUnitPrice', 'Default price', {
                type: 'number',
                step: '0.01',
                min: '0',
                help: 'Fallback only. A customer price always wins.',
              })
            )}
            {field('defaultCogs', 'Default cost of goods', {
              type: 'number',
              step: '0.01',
              min: '0',
            })}

            <Input
              id="notes"
              className="span-2"
              label="Notes"
              multiline
              value={form.notes}
              onChange={(event) => change('notes', event.target.value)}
            />
          </div>

          <div className="btnrow">
            <ButtonLink to="/tests">Cancel</ButtonLink>
            <span className="spacer" />
            <Button type="submit" variant="primary" block disabled={saving}>
              {saving ? 'Saving' : isEdit ? 'Save test type' : 'Add test type'}
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  )
}
